import { useMemo } from 'react';
import { Box } from '@mui/material';
import { OperatorValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import FieldLabel from './FieldLabel';
import { SentenceSelect, SentenceText } from './SentenceParts';
import { CHANNEL_KEYS, ChannelKey, channelPriorityLabels, exclusionKey, parseChannelPriorityValues, parseExclusionKey } from './channelPriority';
import { CLEARED_RULE, RowProps } from './rowProps';

function ChannelPriorityRow({ field, rule, onUpdateField }: RowProps) {
    const { t } = useLanguage();
    const catalog = useMemo(() => parseChannelPriorityValues(field.values), [field.values]);
    const { channelLabel, exclusionLabel } = channelPriorityLabels(t);

    const applied = catalog.priorityOf(rule?.value);
    const optionsChannel = applied?.channel ?? 'email';

    const apply = (channel: ChannelKey, excluded: ChannelKey[] | undefined) => {
        const name = excluded && catalog.nameOf({ channel, excluded });
        if (!name) return;
        const operator = (field.defaultOperator || '=') as OperatorValue;
        onUpdateField(field.name, { operator, value: operator === 'in' ? [name] : name });
    };

    const handleChannelChange = (channel: ChannelKey | '') => {
        if (!channel) return onUpdateField(field.name, CLEARED_RULE);
        apply(channel, catalog.exclusionOptions(channel)[0]);
    };

    const handleExclusionChange = (key: string) => {
        if (!key) return onUpdateField(field.name, CLEARED_RULE);
        apply(optionsChannel, parseExclusionKey(key));
    };

    return (
        <>
            <FieldLabel field={field} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                <SentenceText>{t('pages.clients.queryBuilder.simple.channelPriority.prefix')}</SentenceText>
                <SentenceSelect
                    value={applied?.channel ?? ''}
                    options={CHANNEL_KEYS.map(key => ({ value: key, label: channelLabel(key) }))}
                    minWidth={110}
                    onChange={handleChannelChange}
                />
                {/* Les exclusions dépendent du canal : grisées tant qu'il n'est pas choisi */}
                <SentenceText disabled={!applied}>{t('pages.clients.queryBuilder.simple.channelPriority.connector')}</SentenceText>
                <SentenceSelect
                    value={applied ? exclusionKey(applied.excluded) : ''}
                    disabled={!applied}
                    options={catalog.exclusionOptions(optionsChannel).map(keys => ({ value: exclusionKey(keys), label: exclusionLabel(keys) }))}
                    minWidth={150}
                    onChange={handleExclusionChange}
                />
            </Box>
        </>
    );
}

export default ChannelPriorityRow;
